import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import ClearIcon from "@mui/icons-material/Clear";
import ErrorModal from "../../../../utils/ErrorModal";
import { useNavigate } from "react-router-dom";

const Step2 = ({ sra, setStep, user, data, setData }) => {
  const navigate = useNavigate();
  const [currency, setCurrency] = useState();
  const fileInputRef = useRef(null);
  const [btnLoading, setBtnLoading] = useState(false);
  const [itemLoading, setItemLoading] = useState(true);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState();
  const [fieldErrors, setFieldErrors] = useState({});
  const [dataLoading, setDataLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const handleFileSelect = () => {
    fileInputRef.current.click();
  };
  useEffect(() => {
    const normalizedData = sra?.summaries?.map((item) => ({
      ...item,
      currency: item.currency?.currency || "",
    }));

    setData(normalizedData);
  }, []);

  useEffect(() => {
    const getCurrency = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/currency`,
          { headers: { Authorization: `Bearer ${user.accessToken}` } }
        );
        setCurrency(res.data);
      } catch (error) {
        console.log(error);
      } finally {
        setItemLoading(false);
      }
    };
    getCurrency();
  }, []);

  const handleChange = (index, field, value) => {
    const updatedData = [...data];
    if (field === "isRecommended") {
      updatedData.forEach((row, i) => {
        row.isRecommended = i === index ? true : false;
      });
    }
    if (
      field === "initialTotal" ||
      field === "finalTotal" ||
      field === "total" ||
      field === "lpoTotal"
    ) {
      const num = parseFloat(value);
      updatedData[index][field] = isNaN(num) ? "" : num.toFixed(2);
    } else {
      updatedData[index][field] = value;
    }
    updatedData[index][field] = value;
    setData(updatedData);
    const updatedFieldErrors = { ...fieldErrors };

    if (updatedFieldErrors[index]?.[field]) {
      updatedFieldErrors[index] = {
        ...updatedFieldErrors[index],
        [field]: false,
      };
    }

    setFieldErrors(updatedFieldErrors);
  };

  const addRow = () => {
    setData([
      ...data,
      {
        name: "",
        sob: null,
        initialTotal: "",
        finalTotal: "",
        total: "",
        technical: "",
        commercial: "",
        lpoTotal: "",
        lpoReference: "",
        isRecommended: false,
        currency: "",
      },
    ]);
  };

  const deleteRow = (index) => {
    const updatedData = data.filter((_, i) => i !== index);
    setData(updatedData);
  };

  const exportToExcel = () => {
    const headers = [
      { label: "Supplier Name", key: "name" },
      { label: "SOB(%)", key: "sob" },
      { label: "Currency", key: "currency" },
      { label: "Initial Quote Total", key: "initialTotal" },
      { label: "Final Quote Total", key: "finalTotal" },
      { label: "Total Value Including Taxes", key: "total" },
      { label: "Technical Rating", key: "technical" },
      { label: "Commercial Rating", key: "commercial" },
      { label: "LPO Price Total", key: "lpoTotal" },
      { label: "LPO Reference (PO No.)", key: "lpoReference" },
    ];

    // Format data rows according to backend keys
    const formattedData = data.map((row) => {
      const newRow = {};
      headers.forEach((header) => {
        newRow[header.label] = row[header.key]; // Display header label, fetch backend value
      });
      return newRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sourcing Summary");
    XLSX.writeFile(workbook, "SourcingSummary.xlsx");
  };

  const importFromExcel = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setIsImporting(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const bufferArray = e.target.result;
      const workbook = XLSX.read(bufferArray, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const parsedData = XLSX.utils.sheet_to_json(sheet);

      const headerMapping = {
        "Supplier Name": "name",
        "SOB(%)": "sob",
        Currency: "currency",
        "Initial Quote Total": "initialTotal",
        "Final Quote Total": "finalTotal",
        "Total Value Including Taxes": "total",
        "Technical Rating": "technical",
        "Commercial Rating": "commercial",
        "LPO Price Total": "lpoTotal",
        "LPO Reference (PO No.)": "lpoReference",
      };

      const expectedHeaders = Object.keys(headerMapping);

      const actualHeaders = Object.keys(parsedData[0] || {}).map((h) =>
        h.trim()
      );

      const missingHeaders = expectedHeaders.filter(
        (header) => !actualHeaders.includes(header)
      );

      if (missingHeaders.length > 0) {
        setErrorModal(true);
        setErrorMsg(
          `Please upload Excel in correct format. Missing headers: ${missingHeaders.join(
            ", "
          )}`
        );
        event.target.value = null;
        return;
      }

      const validShortforms = new Set(
        currency.map((unit) => unit.currency?.toLowerCase())
      );

      let hasInvalidCurrency = false;

      const normalize = (str) =>
        str?.toString().replace(/\s+/g, " ").trim().toLowerCase();

      const updatedData = parsedData.map((row) => {
        // Map columns to backend keys
        const mappedRow = {};
        Object.keys(headerMapping).forEach((label) => {
          mappedRow[headerMapping[label]] = row[label];
        });

        const updatedCurrency = mappedRow?.currency ?? row.currency;
        const shortform = normalize(updatedCurrency);
        const isValidShortform = validShortforms.has(shortform);

        if (!isValidShortform) {
          hasInvalidCurrency = true;
          row.currency = ""; // clear invalid unit
        }

        return {
          ...mappedRow,
          currency: updatedCurrency,
        };
      });

      setData(updatedData);
      setIsImporting(false);
      event.target.value = null;
    };
    reader.readAsArrayBuffer(file);
  };

  const validate = (filteredData) => {
    const newFieldErrors = {};

    filteredData?.forEach((item, index) => {
      const errors = {};

      if (!item.name?.toString().trim()) errors.name = true;
      if (
        !item.sob?.toString().trim() ||
        item.sob === null ||
        item.sob === undefined ||
        Number(item.sob) < 0
      )
        errors.sob = true;
      if (!item.currency?.toString().trim()) errors.currency = true;
      if (
        item.initialTotal === null ||
        item.initialTotal === undefined ||
        Number(item.initialTotal) <= 0
      )
        errors.initialTotal = true;
      if (
        item.finalTotal === null ||
        item.finalTotal === undefined ||
        Number(item.finalTotal) <= 0
      )
        errors.finalTotal = true;
      if (
        item.total === null ||
        item.total === undefined ||
        Number(item.total) <= 0
      )
        errors.total = true;
      if (!item.technical?.toString().trim()) errors.technical = true;
      if (!item.commercial?.toString().trim()) errors.commercial = true;
      if (
        item.lpoTotal === null ||
        item.lpoTotal === undefined ||
        Number(item.lpoTotal) <= 0
      )
        errors.lpoTotal = true;
      if (!item.lpoReference?.toString().trim()) errors.lpoReference = true;

      if (Object.keys(errors).length > 0) {
        newFieldErrors[index] = errors;
      }
    });

    setFieldErrors(newFieldErrors);
    return Object.keys(newFieldErrors).length === 0;
  };

  const handleNext = async () => {
    const filteredData = data.filter((item) => {
      const isEmptyRow =
        (item.name?.toString().trim() === "" || item.name === undefined) &&
        (item.name?.toString().trim() === "" ||
          item.sob === null ||
          item.sob === undefined ||
          item.sob < 0) &&
        (item.currency?.toString().trim() === "" ||
          item.currency === undefined) &&
        (item.initialTotal === null ||
          item.initialTotal === undefined ||
          Number(item.initialTotal) <= 0) &&
        (item.finalTotal === null ||
          item.finalTotal === undefined ||
          Number(item.finalTotal) <= 0) &&
        (item.total === null ||
          item.total === undefined ||
          Number(item.total) <= 0) &&
        (item.technical?.toString().trim() === "" ||
          item.technical === undefined) &&
        (item.commercial?.toString().trim() === "" ||
          item.commercial === undefined) &&
        (item.lpoTotal === null ||
          item.lpoTotal === undefined ||
          Number(item.lpoTotal) <= 0) &&
        (item.lpoReference?.toString().trim() === "" ||
          item.lpoReference === undefined);
      return !isEmptyRow;
    });

    if (!validate(filteredData)) {
      return;
    }

    const anyIncompleteRow = filteredData.some((item) => {
      const isFilled =
        item.name?.toString().trim() !== "" &&
        item.sob >= 0 &&
        item.currency?.toString().trim() !== "" &&
        item.initialTotal > 0 &&
        item.finalTotal > 0 &&
        item.total > 0 &&
        item.technical?.toString().trim() !== "" &&
        item.commercial?.toString().trim() !== "" &&
        item.lpoTotal > 0 &&
        item.lpoReference?.toString().trim() !== "";

      return !isFilled;
    });

    if (anyIncompleteRow) {
      setErrorModal(true);
      setErrorMsg(
        "Please check if you have:\n 1. Added atleast one row with complete details.\n2. Filled all the details for every item.\n3. All rates are above 0.\n4. Selected all currencies properly."
      );
      if (!validate(filteredData)) {
        return;
      }
      return;
    }

    const hasRecommended = data?.some((item) => item.isRecommended);
    if (!hasRecommended) {
      setErrorModal(true);
      setErrorMsg("Please select one supplier as the Recommended Supplier.");
      return;
    }

    if (filteredData.length === 0) {
      setErrorModal(true);
      setErrorMsg(
        "Please check if you have:\n 1. Added atleast one row with complete details.\n2. Filled all the details for every item.\n3. All rates are above 0.\n4. Selected all currencies properly."
      );
      return;
    }
    setBtnLoading(true);
    try {
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };
  return (
    <div style={{ position: "relative" }}>
      {isImporting && (
        <div className="loading-div uploading-div">
          Uploading Sheet... <CircularProgress color="#fff"/>
        </div>
      )}
      {btnLoading && <div className="loading-div"></div>}
      <h2>Sourcing Summary</h2>
      <div className="progress-container">
        <span className="blue-bar" style={{ width: "40%" }}></span>
      </div>
      <div className="dashboard-container">
        <table
          border={1}
          style={{
            borderCollapse: "collapse",
            width: "100%",
            marginTop: "10px",
          }}
        >
          <thead>
            <tr>
              <th style={{ padding: "20px", width: "50px" }}>
                Recommended Supplier
              </th>
              <th style={{ width: "150px" }}>Supplier Name</th>
              <th>SOB (%)</th>
              <th style={{ width: "50px" }}>Currency</th>
              <th>Initial Quote Total</th>
              <th>Final Quote Total</th>
              <th>Total Value Including Taxes</th>
              <th>Technical Rating</th>
              <th style={{ width: "50px" }}>Commercial Rating</th>
              <th>LPO Price Total</th>
              <th>LPO Reference (PO No.)</th>
            </tr>
          </thead>
          <tbody>
            {itemLoading ? (
              <Box>Loading...</Box>
            ) : dataLoading ? (
              <Box>Loading...</Box>
            ) : (
              data.map((row, index) => (
                <tr key={index}>
                  <td>
                    <select
                      value={row.isRecommended ? "TRUE" : "FALSE"}
                      onChange={(e) =>
                        handleChange(index, "isRecommended", e.target.value)
                      }
                      className={`table-input ${
                        fieldErrors[index]?.isRecommended && "input-error"
                      }`}
                    >
                      <option value="TRUE">YES</option>
                      <option value="FALSE">NO</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.name || ""}
                      onChange={(e) =>
                        handleChange(index, "name", e.target.value)
                      }
                      className={`table-input ${
                        fieldErrors[index]?.name && "input-error"
                      }`}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={row.sob || ""}
                      onChange={(e) =>
                        handleChange(index, "sob", e.target.value)
                      }
                      className={`table-input ${
                        fieldErrors[index]?.sob && "input-error"
                      }`}
                      style={{ width: "60px" }}
                    />
                  </td>
                  <td>
                    <Autocomplete
                      className={`unit-dropdown ${
                        fieldErrors[index]?.currency && "input-error"
                      }`}
                      options={currency}
                      getOptionLabel={(option) =>
                        `${option.text} (${option.currency})`
                      }
                      value={
                        currency.find((opt) => opt.currency === row.currency) ||
                        null
                      }
                      onChange={(event, newValue) =>
                        handleChange(
                          index,
                          "currency",
                          newValue ? newValue.currency : ""
                        )
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.currency === value.currency
                      }
                      disablePortal
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Choose Currency"
                          variant="outlined"
                          size="small"
                          sx={{
                            "& .MuiInputBase-root": {
                              minHeight: "45px",
                              fontSize: "12px",
                            },
                            "& input": {
                              padding: "15px",
                            },
                          }}
                        />
                      )}
                      filterSelectedOptions
                      autoHighlight
                      selectOnFocus
                      clearOnBlur={false}
                      handleHomeEndKeys
                      disableClearable
                      sx={{ width: "100px" }}
                      slotProps={{
                        paper: {
                          sx: {
                            background: "#D8E9F0",
                            "& .MuiAutocomplete-option": {
                              fontSize: "12px",
                              padding: "6px 12px",
                            },
                            "& .MuiAutocomplete-groupLabel": {
                              fontWeight: "bold",
                              color: "#000",
                              fontSize: "13px",
                              backgroundColor: "#D8E9F0",
                              padding: "0px 6px",
                              lineHeight: "2",
                              position: "static",
                            },
                          },
                        },
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={row.initialTotal || ""}
                      onChange={(e) =>
                        handleChange(index, "initialTotal", e.target.value)
                      }
                      className={`table-input ${
                        fieldErrors[index]?.initialTotal && "input-error"
                      }`}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={row.finalTotal || ""}
                      onChange={(e) =>
                        handleChange(index, "finalTotal", e.target.value)
                      }
                      className={`table-input ${
                        fieldErrors[index]?.finalTotal && "input-error"
                      }`}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={row.total || ""}
                      onChange={(e) =>
                        handleChange(index, "total", e.target.value)
                      }
                      className={`table-input ${
                        fieldErrors[index]?.total && "input-error"
                      }`}
                    />
                  </td>
                  <td>
                    <select
                      value={row.technical || ""}
                      onChange={(e) =>
                        handleChange(index, "technical", e.target.value)
                      }
                      className={`table-input ${
                        fieldErrors[index]?.technical && "input-error"
                      }`}
                      style={{ textAlign: "start" }}
                    >
                      <option value="">Choose</option>
                      <option value="T1">T1</option>
                      <option value="T2">T2</option>
                      <option value="T3">T3</option>
                      <option value="T4">T4</option>
                      <option value="T5">T5</option>
                      <option value="T6">T6</option>
                      <option value="T7">T7</option>
                      <option value="T8">T8</option>
                      <option value="T9">T9</option>
                      <option value="T10">T10</option>
                    </select>
                  </td>
                  <td>
                    <select
                      value={row.commercial || ""}
                      onChange={(e) =>
                        handleChange(index, "commercial", e.target.value)
                      }
                      className={`table-input ${
                        fieldErrors[index]?.commercial && "input-error"
                      }`}
                      style={{ textAlign: "start" }}
                    >
                      <option value="">Choose</option>
                      <option value="L1">L1</option>
                      <option value="L2">L2</option>
                      <option value="L3">L3</option>
                      <option value="L4">L4</option>
                      <option value="L5">L5</option>
                      <option value="L6">L6</option>
                      <option value="L7">L7</option>
                      <option value="L8">L8</option>
                      <option value="L9">L9</option>
                      <option value="L10">L10</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="number"
                      value={row.lpoTotal || ""}
                      onChange={(e) =>
                        handleChange(index, "lpoTotal", e.target.value)
                      }
                      className={`table-input ${
                        fieldErrors[index]?.lpoTotal && "input-error"
                      }`}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.lpoReference || ""}
                      onChange={(e) =>
                        handleChange(index, "lpoReference", e.target.value)
                      }
                      className={`table-input ${
                        fieldErrors[index]?.lpoReference && "input-error"
                      }`}
                    />
                  </td>
                  <td>
                    {/* <Tooltip title="Remove Row"> */}
                    <Button
                      className="btn-contained"
                      onClick={() => deleteRow(index)}
                      sx={{
                        margin: "0 !important",
                        borderRadius: "5px !important",
                        minWidth: "50px !important",
                        width: "0",
                        "&:hover": { background: "#EB2B0B !important" },
                      }}
                    >
                      <ClearIcon />
                    </Button>
                    {/* </Tooltip> */}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <Typography variant="p" fontWeight={700} fontSize={"14px"}>
          [Important Note: <br />
          - Uploading Sheet will overwrite existing work items. <br />- Mention
          only Currency shortform in uppercase in the Currency column while
          uploading from excel sheet.
          <br />- Mention Technical Rating and Commercial Rating in uppercase
          and should be within range (T1 - T10) and (L1 - L10) respectively.]
        </Typography>
        <div
          style={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            gap: 10,
          }}
        >
          <Button className="btn-outlined" onClick={handleFileSelect}>
            Upload Excel
          </Button>
          <Button
            className="btn-outlined"
            onClick={addRow}
            sx={{ padding: "5px 40px !important" }}
          >
            Add New Row
          </Button>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "end",
          }}
        >
          <button
            onClick={exportToExcel}
            style={{
              cursor: "pointer",
              border: "none",
              background: "none",
              borderBottom: "1px solid #000",
            }}
          >
            Download Template Excel
          </button>
        </div>
      </div>
      <input
        type="file"
        accept=".xlsx, .xls"
        ref={fileInputRef}
        onChange={importFromExcel}
        style={{ display: "none" }}
      />
      <div style={{ display: "flex", justifyContent: "end" }}>
        <div
          className="btn-container"
          style={{ gap: 20, margin: "0", justifyContent: "end" }}
        >
          <Button className="btn-outlined" onClick={handleBack}>
            Back
          </Button>
          <Button
            className="btn-contained"
            onClick={handleNext}
            disabled={btnLoading}
          >
            {btnLoading ? "Submitting..." : "Next"}
          </Button>
        </div>
      </div>

      <ErrorModal
        open={errorModal}
        handleClose={() => setErrorModal(false)}
        msg={errorMsg}
      />
    </div>
  );
};

export default Step2;
